/**
 * SetupWizard.jsx
 * ===============
 * Wrapper component for guided setup after app creation
 * Provides progress tracking, navigation, and conditional steps based on app type
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../../store/useAppStore';

const SetupWizard = ({ children, currentStepKey, totalSteps }) => {
  const { appName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAppName } = useAppStore();
  
  const appType = searchParams.get('appType') || 'webapp';

  useEffect(() => {
    if (appName) {
      setAppName(appName);
    }
  }, [appName, setAppName]);

  // Define steps based on app type
  const getStepsForAppType = (type) => {
    const baseSteps = [
      { key: 'welcome', label: 'Welcome', path: 'welcome', allTypes: true },
      { key: 'assets', label: 'Assets', path: 'assets', allTypes: true },
      { key: 'generating', label: 'Generating', path: 'generating', allTypes: true }, // NEW: AI generation step
      { key: 'data', label: 'Data', path: 'data', types: ['webapp', 'enterprise'] },
      { key: 'pages', label: 'Pages', path: 'pages', allTypes: true },
      { key: 'integrations', label: 'Integrations', path: 'integrations', types: ['static'] },
      { key: 'validations', label: 'Validations', path: 'validations', types: ['webapp', 'enterprise'] },
      { key: 'actions', label: 'Actions', path: 'actions', types: ['webapp', 'enterprise'] },
      { key: 'processes', label: 'Processes', path: 'processes', types: ['enterprise'] },
      { key: 'complete', label: 'Complete', path: 'complete', allTypes: true }
    ];

    return baseSteps.filter(step => 
      step.allTypes || (step.types && step.types.includes(type))
    );
  };

  const steps = getStepsForAppType(appType);
  const currentStepIndex = steps.findIndex(step => step.key === currentStepKey);
  const currentStep = currentStepIndex + 1;

  // Validate that we found the current step
  if (currentStepIndex === -1 && currentStepKey !== 'complete') {
    console.warn(`[SetupWizard] Step "${currentStepKey}" not found for appType "${appType}". Available steps:`, steps.map(s => s.key));
  }

  const handleNext = () => {
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length && steps[nextStepIndex]) {
      const nextStep = steps[nextStepIndex];
      navigate(`/setup-wizard/${appName}/${nextStep.path}?appType=${appType}`, { replace: true });
    } else {
      console.warn('[SetupWizard] No next step available');
    }
  };

  const handleSkip = () => {
    // Skip current step and go to next step
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length && steps[nextStepIndex]) {
      const nextStep = steps[nextStepIndex];
      console.log(`[SetupWizard] Skipping to next step: ${nextStep.key} (${nextStep.path})`);
      navigate(`/setup-wizard/${appName}/${nextStep.path}?appType=${appType}`, { replace: true });
    } else {
      // If already on last step, go to complete
      console.log('[SetupWizard] On last step, going to complete');
      navigate(`/setup-wizard/${appName}/complete?appType=${appType}`, { replace: true });
    }
  };

  const handleSkipToEnd = () => {
    // Skip all remaining steps and go directly to complete
    // DISABLED FOR NOW - per user request
    navigate(`/setup-wizard/${appName}/complete?appType=${appType}`, { replace: true });
  };

  const handleBack = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0 && steps[prevStepIndex]) {
      const prevStep = steps[prevStepIndex];
      navigate(`/setup-wizard/${appName}/${prevStep.path}?appType=${appType}`, { replace: true });
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#F9FAFB' }}>
      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '2px solid #E5E7EB',
        padding: '16px 24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#111827' }}>
              Setting up {appName}
            </h1>
            <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
              {appType === 'static' ? '🌐 Static Website' : 
               appType === 'webapp' ? '📱 Web Application' : 
               '🏢 Enterprise Application'}
            </p>
          </div>
          
          {/* Change App Type Button */}
          <button
            onClick={() => navigate(`/create-app?edit=${appName}&currentType=${appType}`)}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#6B7280',
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            🔄 Change App Type
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #E5E7EB',
        padding: '20px 24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
              Step {currentStep} of {steps.length}
            </span>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>
              {Math.round((currentStep / steps.length) * 100)}% Complete
            </span>
          </div>
          
          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#E5E7EB',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${(currentStep / steps.length) * 100}%`,
              height: '100%',
              backgroundColor: appType === 'static' ? '#10B981' : 
                             appType === 'webapp' ? '#3B82F6' : '#EC4899',
              transition: 'width 0.3s ease'
            }} />
          </div>

          {/* Step labels */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
            {steps.map((step, index) => (
              <div
                key={step.key}
                style={{
                  fontSize: '12px',
                  color: index < currentStepIndex ? '#10B981' : 
                         index === currentStepIndex ? '#111827' : '#9CA3AF',
                  fontWeight: index === currentStepIndex ? '600' : '400'
                }}
              >
                {step.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {children}
        </div>
      </div>

      {/* Footer Navigation */}
      <div style={{
        backgroundColor: 'white',
        borderTop: '1px solid #E5E7EB',
        padding: '16px 24px'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <button
            onClick={handleBack}
            disabled={currentStepIndex === 0}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              color: currentStepIndex === 0 ? '#9CA3AF' : '#374151',
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: currentStepIndex === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            ← Back
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            {currentStepKey !== 'complete' && (
              <>
                {currentStepKey !== 'welcome' && (
                  <button
                    onClick={handleSkip}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6B7280',
                      backgroundColor: 'white',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Skip Step
                  </button>
                )}
                {/* Skip All button disabled per user request */}
                {false && (
                  <button
                    onClick={handleSkipToEnd}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6B7280',
                      backgroundColor: 'white',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    {currentStepKey === 'welcome' ? 'Skip Setup' : 'Skip All'}
                  </button>
                )}
              </>
            )}
            
            {currentStepKey !== 'complete' && (
              <button
                onClick={handleNext}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'white',
                  backgroundColor: appType === 'static' ? '#10B981' : 
                                  appType === 'webapp' ? '#3B82F6' : '#EC4899',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Continue →
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupWizard;

